// Bug Test Dashboard — shared client constants, types + pure helpers.
//
// Consumes the LOCKED API CONTRACT (see
// docs/superpowers/plans/2026-07-20-bug-test-dashboard.md). Status enum is the
// single source of truth mirrored from backend BUG_FIX_STATUSES; the approve
// lock (awaiting_approval|failed) matches backend canApprove().
//
// Mirrors the SupportTicketsPage idiom: inline STATUS_STYLES pill map, no
// react-query / toast / shared-primitive lib.

export type BugTestPlatform = 'web' | 'mobile' | 'backend';
export type BugTestStatus =
  | 'queued' | 'analyzing' | 'reproducing' | 'fixing' | 'verifying'
  | 'awaiting_approval' | 'approved' | 'deploying' | 'deployed'
  | 'failed' | 'rejected' | 'needs_info';

export const BUG_FIX_STATUSES: BugTestStatus[] = [
  'queued', 'analyzing', 'reproducing', 'fixing', 'verifying',
  'awaiting_approval', 'approved', 'deploying', 'deployed',
  'failed', 'rejected', 'needs_info',
];

export interface AgentAction { ts: string; phase: string; title: string; detail?: string | null; }
export interface VerifyResults { analyzeDelta?: number; smokeTotal?: number; smokePassed?: number; newTests?: string[]; typecheckBaseline?: number; notes?: string; }
export interface DeployChecks { backupPath?: string; buildClean?: boolean; health200?: boolean; routesMounted?: boolean; schemaChange?: boolean; }
export interface DeployInfo { initiatedAt?: string; initiatedByName?: string; result?: string; deployedAt?: string; log?: string; apkUrl?: string; otaVersion?: string; testDevice?: string; otaChannel?: string; }
export interface LinkedTicket { number?: string; subject?: string; module?: string | null; priority?: string; createdById?: string | null; }

export interface BugTestListItem {
  id: string;
  status: BugTestStatus;
  platform: string;
  confidence?: string | null;
  updatedAt: string;
  supportTicket?: LinkedTicket;
}
export interface BugTestDetail extends BugTestListItem {
  branchName?: string | null;
  prUrl?: string | null;
  devVerifyUrl?: string | null;
  reproSteps?: string | null;
  rootCause?: string | null;
  agentActions?: AgentAction[] | null;
  verifyResults?: VerifyResults | null;
  deployChecks?: DeployChecks | null;
  deploy?: DeployInfo | null;
  rejectionReason?: string | null;
}

export function platformBadge(platform?: string): { label: string; emoji: string; className: string } {
  switch (platform) {
    case 'mobile':  return { label: 'Mobile',  emoji: '📱', className: 'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300' };
    case 'backend': return { label: 'Backend', emoji: '⚙️', className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' };
    case 'web':     return { label: 'Web',     emoji: '🌐', className: 'bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300' };
    default:        return { label: 'Unclassified', emoji: '❔', className: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' };
  }
}

export const STATUS_STYLES: Record<string, string> = {
  queued: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
  analyzing: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  reproducing: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  fixing: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300',
  verifying: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  awaiting_approval: 'bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300',
  approved: 'bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-300',
  deploying: 'bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-300',
  deployed: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
  needs_info: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300',
};

export const STATUS_LABEL: Record<string, string> = {
  awaiting_approval: 'Awaiting approval',
  needs_info: 'Needs info',
};
export const statusLabel = (s: string) => STATUS_LABEL[s] || s.replace(/_/g, ' ');

export function canApprove(status: string): boolean { return status === 'awaiting_approval' || status === 'failed'; }
export function isLockedDeploying(status: string): boolean { return status === 'approved' || status === 'deploying'; }
export function isTerminal(status: string): boolean { return status === 'deployed' || status === 'rejected'; }

export { isSupportDeskRole } from './supportConstants';
